(() => {
  const DESKTOP_QUERY = '(min-width: 1024px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const VISIBLE_COUNT = 3;
  const REWIND_MS = 1200;
  const DIM_MS = 400;
  const ROW_GAP = 32;
  const ROW_MIN = 132;
  const STEP_DEFAULT = 3;
  const HOLD_DEFAULT = 9;
  const RESIZE_DEBOUNCE = 150;

  class TimelineMilestones extends HTMLElement {
    connectedCallback() {
      this.cards = [...this.querySelectorAll('[data-timeline-card]')];
      this.track = this.querySelector('[data-timeline-track]');
      if (this.cards.length === 0 || !this.track) return;

      this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
      this.motionQuery = window.matchMedia(REDUCED_MOTION_QUERY);

      // Rows must share one height or the vertical steps and rail segments come
      // out ragged. The details block keeps its natural height, so it stays
      // measurable after the row height has been applied.
      this.measure = () => {
        let tallest = 0;
        for (const card of this.cards) {
          const details = card.querySelector('[data-timeline-details]');
          if (details) tallest = Math.max(tallest, details.offsetHeight);
        }

        this.rowHeight = Math.max(tallest + ROW_GAP, ROW_MIN);
        this.style.setProperty('--timeline-row-h', `${this.rowHeight}px`);
      };

      this.applyOffset = (index) => {
        const maxIndex = Math.max(0, this.cards.length - VISIBLE_COUNT);
        const unit = this.desktopQuery.matches ? this.cards[0].offsetWidth : this.rowHeight;
        this.style.setProperty('--timeline-offset', `${Math.min(index, maxIndex) * unit}px`);
      };

      this.showAll = () => {
        for (const card of this.cards) card.classList.add('is-lit');
        this.style.setProperty('--timeline-offset', '0px');
      };

      this.schedule = (delay, action) => {
        this.clearTimer();
        this.pending = action;
        this.remaining = delay;
        this.startedAt = performance.now();
        this.timer = window.setTimeout(() => {
          this.timer = null;
          this.pending = null;
          action();
        }, delay);
      };

      this.clearTimer = () => {
        if (!this.timer) return;
        window.clearTimeout(this.timer);
        this.timer = null;
      };

      this.pause = () => {
        if (this.paused) return;
        this.paused = true;
        this.classList.add('is-paused');
        if (!this.timer) return;
        this.remaining = Math.max(0, this.remaining - (performance.now() - this.startedAt));
        this.clearTimer();
      };

      this.unpause = () => {
        this.paused = false;
        this.classList.remove('is-paused');
      };

      this.step = () => {
        this.cards[this.index].classList.add('is-lit');
        this.applyOffset(this.index);

        if (this.index === this.cards.length - 1) {
          this.schedule(this.stepMs + this.holdMs, this.dim);
          return;
        }

        this.schedule(this.stepMs, this.advance);
      };

      this.advance = () => {
        this.index += 1;
        this.step();
      };

      this.dim = () => {
        for (const card of this.cards) card.classList.remove('is-lit');
        this.schedule(DIM_MS, this.rewind);
      };

      // Class and transform change in the same task, so the longer rewind
      // duration is already in effect when the transform transition starts.
      this.rewind = () => {
        this.track.classList.add('is-rewinding');
        this.applyOffset(0);
        this.schedule(REWIND_MS, this.restart);
      };

      this.restart = () => {
        this.track.classList.remove('is-rewinding');
        this.index = 0;
        this.step();
      };

      this.handleIntersect = (entries) => {
        for (const entry of entries) this.inView = entry.isIntersecting;
        this.syncPlayback();
      };

      // The first start has to clear the paused state too: the observer reports a
      // non-intersecting entry before the section scrolls in, and a lingering
      // is-paused would freeze every fill while the timers kept stepping.
      this.syncPlayback = () => {
        if (!this.inView || document.hidden || this.editorHold) {
          this.pause();
          return;
        }

        if (!this.started) {
          this.started = true;
          this.unpause();
          this.step();
          return;
        }

        if (!this.paused) return;

        this.unpause();
        if (this.pending) this.schedule(this.remaining, this.pending);
        else this.step();
      };

      this.handleResize = () => {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => {
          this.measure();
          this.applyOffset(this.index);
        }, RESIZE_DEBOUNCE);
      };

      this.handleMotionChange = () => {
        if (!this.motionQuery.matches) return;
        this.clearTimer();
        this.pending = null;
        this.observer?.disconnect();
        this.showAll();
      };

      // Editor only: keep a merchant's selected milestone in view while they edit
      // it, since nothing on the storefront can reach the off-screen ones.
      this.handleBlockSelect = (event) => {
        const selected = this.cards.find((card) => card === event.target || card.contains(event.target));
        if (!selected) return;

        this.editorHold = true;
        this.pause();
        this.index = this.cards.indexOf(selected);

        for (const [position, card] of this.cards.entries()) {
          card.classList.toggle('is-lit', position <= this.index);
        }

        this.applyOffset(this.index);
      };

      this.handleBlockDeselect = (event) => {
        if (!this.cards.some((card) => card === event.target || card.contains(event.target))) return;

        this.editorHold = false;
        this.clearTimer();
        this.pending = null;
        this.syncPlayback();
      };

      this.controller = new AbortController();
      const { signal } = this.controller;

      this.stepMs = (Number.parseFloat(this.dataset.timelineStep) || STEP_DEFAULT) * 1000;
      this.holdMs = (Number.parseFloat(this.dataset.timelineHold) || HOLD_DEFAULT) * 1000;

      this.index = 0;
      this.started = false;
      this.paused = false;
      this.inView = false;
      this.editorHold = false;
      this.pending = null;
      this.timer = null;
      this.remaining = 0;
      this.startedAt = 0;

      this.style.setProperty('--timeline-step', `${this.stepMs}ms`);
      this.style.setProperty('--timeline-rewind', `${REWIND_MS}ms`);

      this.measure();
      this.applyOffset(0);

      window.addEventListener('resize', this.handleResize, { signal });
      document.addEventListener('visibilitychange', this.syncPlayback, { signal });
      document.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });
      document.addEventListener('shopify:block:deselect', this.handleBlockDeselect, { signal });
      this.motionQuery.addEventListener('change', this.handleMotionChange, { signal });

      if (this.motionQuery.matches) {
        this.showAll();
        return;
      }

      this.observer = new IntersectionObserver(this.handleIntersect, { threshold: 0.2 });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      window.clearTimeout(this.resizeTimer);
      this.clearTimer?.();
      this.observer?.disconnect();
      this.controller?.abort();
    }
  }

  if (!customElements.get('timeline-milestones')) {
    customElements.define('timeline-milestones', TimelineMilestones);
  }
})();
